import React from 'react';
import cx from 'classnames';
import config from '@plone/volto/registry';
import withBlockExtensions from '@plone/volto/helpers/Extensions/withBlockExtensions';
import DefaultView from './DefaultView';

import type { MultiButtonsBlockData, MultiButtonsVariant } from './index';

interface Props {
  data: MultiButtonsBlockData;
  className?: string;
  isEditMode?: boolean;
  style?: React.CSSProperties;
}

type ThemeDefinition = {
  name: string;
  style: Record<string, string>;
};

function getThemeDefinitions(blockType?: string): ThemeDefinition[] {
  const blockThemes = blockType
    ? (config.blocks?.blocksConfig as any)?.[blockType]?.themes
    : undefined;

  return (blockThemes ||
    (config.blocks as any)?.themes ||
    []) as ThemeDefinition[];
}

function resolveThemeStyle(
  theme: unknown,
  themeDefinitions: ThemeDefinition[],
): React.CSSProperties {
  if (!theme) return {};

  if (typeof theme === 'string') {
    return themeDefinitions.find((t) => t.name === theme)?.style ?? {};
  }

  if (typeof theme === 'object') {
    return theme as React.CSSProperties;
  }

  return {};
}

const View: React.FC<Props> = ({ data, className, isEditMode, style }) => {
  const items = Array.isArray(data.buttons) ? data.buttons : [];
  const variant = ((data as any)?.variant || 'default') as MultiButtonsVariant;

  const blockType = (data as any)?.['@type'] as string | undefined;
  const themeDefinitions = getThemeDefinitions(blockType);
  const themeValue = (data as any)?.theme ?? (data as any)?.styles?.theme;
  const themeStyle = resolveThemeStyle(themeValue, themeDefinitions);
  const mergedStyle = {
    ...(style || {}),
    ...themeStyle,
  } as React.CSSProperties;

  if (!items.length && !isEditMode) return null;

  const maxColumnsRaw = Number.isFinite(data.maxColumns)
    ? Math.max(0, Math.floor(data.maxColumns as number))
    : 0;
  const maxColumns =
    maxColumnsRaw > 0
      ? Math.min(maxColumnsRaw, items.length)
      : Math.max(1, items.length);
  const stretch = data.stretch ?? false;
  const themeColor = (data as any).themeColor;

  return (
    <div
      className={cx(
        'block multi-buttons-block',
        variant ? `is-variant-${variant}` : null,
        className,
      )}
      style={mergedStyle}
    >
      <DefaultView
        items={items}
        isEditMode={isEditMode ?? false}
        maxColumns={maxColumns}
        stretch={stretch}
        variant={variant}
        themeColor={themeColor}
      />
    </div>
  );
};

export default withBlockExtensions(View);
